import { ApiService } from '@/services/api.services';
import { formatDateIndo } from '@/utils/formatDate';
import { formatViews } from '@/utils/formatViews';
import { Calendar, Eye, Folder } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProtectImage from '../protect/ProtectImage';
import SidebarPopularSkeleton from '../skeleton/SidebarPopularSkeleton';
import { BodySm, BodySmHTML } from '../ui/typography/Typography';

export default function SidebarPopularNews({ refreshKey = 0 }) {
  const [popular, setPopular] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPopularNews() {
      setLoading(true);
      try {
        const res = await ApiService.getPopularNews();
        const data = res?.data || [];

        const sorted = [...data].sort((a, b) => (b.views || 0) - (a.views || 0));

        setPopular(sorted);
      } catch (error) {
        console.error('Error fetching popular news:', error);
        setPopular([]);
      } finally {
        setLoading(false);
      }
    }

    fetchPopularNews();
  }, [refreshKey]);

  if (loading) {
    return (
      <>
        <SidebarPopularSkeleton />
      </>
    );
  }

  return (
    <aside className="w-full bg-white rounded-md lg:rounded-xl border border-gray-200 px-3 lg:px-5 py-3 lg:py-4 gap-2 lg:gap-5 sticky top-24 mb-5">
      <h3 className="text-md lg:text-lg font-semibold mb-4">Berita Populer</h3>

      {/* Skeleton */}
      {popular.length === 0 ? (
        <p className="text-sm text-gray-500">Belum ada berita populer.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {popular.map((item, i) => (
            <div key={item.id}>
              <Link to={`/berita/${item.slug}`} className="group flex gap-3 items-start">
                <div className="w-12 lg:w-16 aspect-square rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <ProtectImage
                    src={item.thumbnail_small || item.thumbnail}
                    alt={item.title}
                    fill
                  />
                </div>

                <div>
                  <BodySm className="line-clamp-2 font-semibold group-hover:text-accent-600">
                    {item.title}
                  </BodySm>
                  <BodySmHTML
                    content={item.content}
                    className="text-xs line-clamp-1 group-hover:text-accent-600"
                  />
                </div>
              </Link>
              <div className="flex items-center flex-wrap gap-1 my-2">
                <div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 text-[10px]">
                  <Folder size={12} />
                  <span>{item.category?.name || 'Uncategorized'}</span>
                </div>

                <div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 text-[10px]">
                  <Calendar size={12} />
                  <span>{formatDateIndo(item.created_at)}</span>
                </div>

                <div className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 text-[10px]">
                  <Eye size={12} />
                  <span>{formatViews(item.views)}</span>
                </div>
              </div>

              {/* Divider */}
              {i < popular.length - 1 && <hr className="border-t border-gray-200 my-3" />}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}
